<template>
  <base-sticky v-slot="{ isStuck }" name="headerPLP">
    <div
      v-style:top="{
        sm: floatingNavigation ? '0' : pxToRem(header.sticky.offset - 1),
        lg: pxToRem(header.sticky.offset - 1),
      }"
      class="sticky z-above-header grid cols-[1fr_auto] transform gap-x-4 bg-white px-4 py-2 duration lg:gap-y-2 lg:px-6"
      :class="{ 'shadow-bottom': isStuck }"
    >
      <div v-if="productsLoading" class="h-6 w-64 skeleton lg:col-span-2 lg:h-8 lg:w-75" />
      <h1 v-else-if="hasTitle" class="body-1 lg:col-span-2">
        {{ props.title }}
      </h1>
      <div v-if="$feature.enablePLPESpots && (eSpotsPending || eSpot)" class="md:col-span-2 <md:hidden">
        <!-- Only show e-spot on initial load of PLP and not while PLP is being filtered -->
        <div v-if="eSpotsPending" v-style:h="{ sm: '8.5rem', md: '17.25rem', lg: '23.26rem' }" class="skeleton" />
        <cms-section v-else-if="eSpot" :section="eSpot" />
      </div>
      <div class="flex items-center <lg:row-span-2">
        <base-button
          :aria-expanded="filtersShown"
          class="flex items-center gap-1"
          data-test-id="product-list-header-filters-button"
          @click="onFiltersToggle"
        >
          <span class="<lg:sr-only">{{ filtersShown ? $t.hideFilters : $t.showFilters }}</span>
          <vf-icon name="filters" size="md" />
        </base-button>
      </div>

      <div aria-live="polite">
        <div v-if="productsLoading" class="h-3 w-12 skeleton <lg:mt-1 lg:h-5 lg:w-18" />
        <p v-else class="body-4 lg:body-3">
          {{ total }} {{ $pluralize($t.items, total) }}
        </p>
      </div>
    </div>
    <panel-filters v-slot="attrs">
      <slot v-bind="{ ...attrs, openedSections }" />
    </panel-filters>
  </base-sticky>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/list/header/Header.vue'
import type { PlpAboveGridBelowHeadingEspotContext } from '#content/contexts/plp'
import { PlpAboveGridBelowHeadingEspotContextKey } from '#content/contexts/plp'

const props = defineProps<Props>()
defineEmits<Emits>()

const { floatingNavigation } = useAppConfig().components.vf.header
const {
  eSpotsPending,
  eSpot
} = inject(PlpAboveGridBelowHeadingEspotContextKey, {} as PlpAboveGridBelowHeadingEspotContext)

const PanelFilters = createPanel('filters')

const { $t, $viewport } = useNuxtApp()

const header = useHeaderStore()
const filtersShown = defineModel('filtersShown')
const openedSections = defineModel<Partial<Record<string, boolean>>>('openedSections')
const hasTitle = computed(() => hasTextContent(props.title))
const total = computed(() => props.list?.total || 0)

const onFiltersToggle = () => {
  if ($viewport.lg)
    filtersShown.value = !filtersShown.value
  else
    PanelFilters.open()
}
</script>
